import React, { useMemo, useState } from 'react';
import { intersection, isEmpty } from 'lodash';

import { Heading } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import PlusIcon from '~/components/icons/PlusIcon';
import LoadingIndicator from '~/components/LoadingIndicator';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  FundAccountEngineConfigInfo,
  FundAccountRuleCreateParams,
  FundAccountRuleInfo,
} from '~/server_shared/openapi/data-contracts';

import CardDialog from '../../../CardDialog';
import { useSysconfig } from '../../SystemConfigurationScreen';

import {
  createFundAccountRule,
  deleteFundAccountRule,
  engineConfigUrl,
  fundAccountsBaseUrl,
  reorderFundAccountRules,
  setFundAccountRuleEnabled,
  updateFundAccountRule,
} from './api/fundAccountRulesApi';
import FundAccountDefaultBehaviorCard from './components/FundAccountDefaultBehaviorCard';
import FundAccountRulesFilters from './components/FundAccountRulesFilters';
import FundAccountRulesTable from './components/FundAccountRulesTable';
import AddOrEditFundAccountRuleDialog from './AddOrEditFundAccountRuleDialog';
import type { FundAccountRuleFilterParams, SysconfigOrganization } from './types';

const FundAccounts: React.FC = () => {
  const { organization } = useSysconfig() as { organization: SysconfigOrganization };
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<FundAccountRuleInfo | null>(null);
  const [filteredParams, setFilteredParams] = useState<FundAccountRuleFilterParams>({
    searchTerm: '',
    subOrgIds: [],
    lobs: [],
  });

  // Both reads go through the module's own URL builders rather than a literal: this module is
  // mounted under `/sysconfig/api/v1`, not the `/api/v1` its Guardrails sibling uses, so a
  // hand-written path here would 404 on mount and surface only as a permanent error state.
  const {
    isLoading: isLoadingRules,
    isError: isErrorRules,
    data: rules,
    reloadData: reloadRules,
  } = useDataFetcher<FundAccountRuleInfo[]>(`${fundAccountsBaseUrl(organization.id)}/rules`);

  const {
    isLoading: isLoadingConfig,
    isError: isErrorConfig,
    data: engineConfig,
    reloadData: reloadEngineConfig,
  } = useDataFetcher<FundAccountEngineConfigInfo>(engineConfigUrl(organization.id));

  // Local order so a drop reorders instantly; resynced whenever the fetched data changes, which
  // also reverts a failed reorder. The resync happens during render (not in an effect) so the new
  // order is reflected in the same paint — otherwise the empty-state/list gate below would read a
  // stale empty `orderedRules` for one post-load paint and briefly flash "No fund account rules
  // configured yet." over an organization that has rules. Same shape as the ClaimStage sibling.
  const [orderedRules, setOrderedRules] = useState<FundAccountRuleInfo[]>(rules ?? []);
  const [syncedRules, setSyncedRules] = useState(rules);
  if (syncedRules !== rules) {
    setSyncedRules(rules);
    setOrderedRules(rules ?? []);
  }

  const hasActiveFilters =
    !!filteredParams.searchTerm || !isEmpty(filteredParams.subOrgIds) || !isEmpty(filteredParams.lobs);

  const filteredRules = useMemo(() => {
    let result = orderedRules;
    if (!isEmpty(filteredParams.subOrgIds)) {
      result = result.filter(
        (rule) =>
          rule.scope?.is_all_sub_orgs ||
          !isEmpty(intersection(rule.scope?.sub_organization_ids ?? [], filteredParams.subOrgIds))
      );
    }
    if (!isEmpty(filteredParams.lobs)) {
      result = result.filter((rule) => {
        const ruleLobs = rule.scope?.lobs ?? [];
        return rule.scope?.is_all_lobs || intersection(filteredParams.lobs, ruleLobs).length > 0;
      });
    }
    if (filteredParams.searchTerm) {
      const term = filteredParams.searchTerm.toLowerCase();
      result = result.filter(
        (rule) =>
          (rule.name ?? '').toLowerCase().includes(term) || (rule.account_number ?? '').toLowerCase().includes(term)
      );
    }
    return result;
  }, [orderedRules, filteredParams]);

  const withProcessing = async (fn: () => Promise<void>) => {
    setIsProcessing(true);
    try {
      await fn();
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitRule = async (values: FundAccountRuleCreateParams) => {
    await withProcessing(async () => {
      if (ruleToEdit) {
        await updateFundAccountRule(organization.id, ruleToEdit.id, values);
      } else {
        await createFundAccountRule(organization.id, values);
      }
    });
  };

  // Delete/enable are invoked fire-and-forget from the row (ThreeDotsMenu / Switch), so swallow
  // after the service has already reported the error — otherwise the rethrow from
  // `withProcessing` becomes an unhandled promise rejection. (The create/update path keeps
  // rethrowing so the dialog's Formik catch can react.)
  const handleDeleteRule = async (rule: FundAccountRuleInfo) => {
    try {
      await withProcessing(() => deleteFundAccountRule(organization.id, rule.id));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  const handleEnabledChanged = async (rule: FundAccountRuleInfo) => {
    try {
      await withProcessing(() => setFundAccountRuleEnabled(organization.id, rule.id, !rule.is_enabled));
    } catch {
      /* already surfaced via reportAxiosError */
    }
  };

  // `fromIndex`/`toIndex` are positions in `filteredRules` — the list handed to `DndOrderedList`
  // — while the splice below applies them to `orderedRules`. That is correct *only* because
  // dragging is disabled whenever a filter is active (see FundAccountRulesTable): the two lists
  // coincide, so the indices mean the same thing in both. That gate is the load-bearing
  // invariant here, not the choice of array. The payload itself must be the whole live order,
  // because the endpoint rejects anything that is not an exact permutation of every live rule.
  const handleReorder = async ({ fromIndex, toIndex }: DndOrderedListReorder) => {
    const reordered = [...orderedRules];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setOrderedRules(reordered);

    setIsProcessing(true);
    try {
      await reorderFundAccountRules(
        organization.id,
        reordered.map((rule) => rule.id)
      );
      await reloadRules();
    } catch {
      // The BE reorders in one transaction and rolls the whole thing back on rejection, so
      // there is no partial state to keep — revert to the server order.
      setOrderedRules(rules ?? []);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditRuleClicked = (rule: FundAccountRuleInfo) => {
    setRuleToEdit(rule);
    setIsRuleDialogOpen(true);
  };

  const handleCloseRuleDialog = () => {
    setIsRuleDialogOpen(false);
    setRuleToEdit(null);
  };

  const emptyStateText = hasActiveFilters
    ? 'No fund account rules match the current filters.'
    : 'No fund account rules configured yet.';

  const isLoading = isLoadingRules || isLoadingConfig;
  const isError = isErrorRules || isErrorConfig;
  if (isLoading || isError || !engineConfig) {
    return <LoadingIndicator isError={isError} />;
  }

  return (
    <CardDialog noCardTitle>
      <div className="p-20">
        <Heading variant={Heading.TYPES.H3} className="pb-12">
          Fund Accounts
        </Heading>
        <FundAccountDefaultBehaviorCard
          organizationId={organization.id}
          config={engineConfig}
          onSaved={async () => {
            await reloadEngineConfig();
          }}
        />
        <div className="flex justify-end pb-12">
          <Button
            color="primary"
            onClick={() => setIsRuleDialogOpen(true)}
            startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
          >
            Add Rule
          </Button>
        </div>
        <FundAccountRulesFilters
          organization={organization}
          subOrgIds={filteredParams.subOrgIds}
          onSearchTermChanged={(searchTerm) => setFilteredParams((p) => ({ ...p, searchTerm }))}
          onSubOrgChanged={(subOrgIds) => setFilteredParams((p) => ({ ...p, subOrgIds }))}
          onLobsChanged={(lobs) => setFilteredParams((p) => ({ ...p, lobs }))}
        />
        <FundAccountRulesTable
          organization={organization}
          rules={filteredRules}
          isProcessing={isProcessing}
          isReorderDisabled={hasActiveFilters}
          emptyStateText={emptyStateText}
          onReorder={handleReorder}
          onEditRule={handleEditRuleClicked}
          onDeleteRule={handleDeleteRule}
          onEnabledChanged={handleEnabledChanged}
        />
      </div>
      {isRuleDialogOpen ? (
        <AddOrEditFundAccountRuleDialog
          onCancel={handleCloseRuleDialog}
          onSubmit={handleSubmitRule}
          rule={ruleToEdit}
        />
      ) : null}
    </CardDialog>
  );
};

export default FundAccounts;
